import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Phone, Plus, Eye, Clock, User } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const criticidadeLabels = {
    baixa: 'Baixa',
    media: 'Média',
    alta: 'Alta',
    critica: 'Crítica'
};

const statusLabels = {
    aberto: 'Aberto',
    em_tratativa: 'Em Tratativa',
    encerrado: 'Encerrado'
};

const PAPage = () => {
    const [pas, setPas] = useState([]);
    const [roos, setRoos] = useState([]);
    const [sinistros, setSinistros] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedPA, setSelectedPA] = useState(null);
    const { hasRole } = useAuth();

    const [formData, setFormData] = useState({
        titulo: '',
        descricao: '',
        criticidade: 'media',
        roo_id: '',
        sinistro_id: '',
        contatos: []
    });

    const [newContato, setNewContato] = useState({ nome: '', cargo: '', telefone: '', email: '' });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        try {
            const [pasRes, roosRes, sinistrosRes] = await Promise.all([
                axios.get(`${API}/pas`),
                axios.get(`${API}/roos`),
                axios.get(`${API}/sinistros`)
            ]);
            setPas(pasRes.data);
            setRoos(roosRes.data.filter(r => r.status !== 'encerrado'));
            setSinistros(sinistrosRes.data.filter(s => s.status !== 'encerrado'));
        } catch (error) {
            toast.error('Erro ao carregar dados');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.roo_id && !formData.sinistro_id) {
            toast.error('PA deve estar vinculado a ROO ou Sinistro');
            return;
        }

        try {
            await axios.post(`${API}/pas`, formData);
            toast.success('Protocolo de Acionamento criado!');
            setDialogOpen(false);
            resetForm();
            fetchData();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao criar PA');
        }
    };

    const handleStatusChange = async (paId, newStatus) => {
        try {
            await axios.put(`${API}/pas/${paId}`, { status: newStatus });
            toast.success('Status atualizado!');
            fetchData();
        } catch (error) {
            toast.error('Erro ao atualizar status');
        }
    };

    const addContato = () => {
        if (!newContato.nome || !newContato.telefone) {
            toast.error('Nome e telefone são obrigatórios');
            return;
        }
        setFormData({
            ...formData,
            contatos: [...formData.contatos, { ...newContato }]
        });
        setNewContato({ nome: '', cargo: '', telefone: '', email: '' });
    };

    const removeContato = (index) => {
        setFormData({
            ...formData,
            contatos: formData.contatos.filter((_, i) => i !== index)
        });
    };

    const resetForm = () => {
        setFormData({
            titulo: '',
            descricao: '',
            criticidade: 'media',
            roo_id: '',
            sinistro_id: '',
            contatos: []
        });
        setNewContato({ nome: '', cargo: '', telefone: '', email: '' });
    };

    const canCreate = hasRole(['admin', 'gestor', 'operador']);
    const canValidate = hasRole(['admin', 'gestor']);

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        return new Date(dateStr).toLocaleString('pt-BR');
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-cyan-400">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="pa-page">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                        Protocolo de Acionamento
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Gestão de acionamentos e contatos de emergência
                    </p>
                </div>
                
                {canCreate && (
                    <Dialog open={dialogOpen} onOpenChange={(open) => {
                        setDialogOpen(open);
                        if (!open) resetForm();
                    }}>
                        <DialogTrigger asChild>
                            <Button 
                                data-testid="btn-new-pa"
                                className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Novo PA
                            </Button>
                        </DialogTrigger>
                        <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
                            <DialogHeader>
                                <DialogTitle className="font-heading text-xl text-white">
                                    Novo Protocolo de Acionamento
                                </DialogTitle>
                            </DialogHeader>
                            
                            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Título *
                                    </Label>
                                    <Input
                                        data-testid="input-pa-titulo"
                                        value={formData.titulo}
                                        onChange={(e) => setFormData({...formData, titulo: e.target.value})}
                                        required
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Criticidade
                                        </Label>
                                        <Select 
                                            value={formData.criticidade} 
                                            onValueChange={(value) => setFormData({...formData, criticidade: value})}
                                        >
                                            <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                {Object.entries(criticidadeLabels).map(([key, label]) => (
                                                    <SelectItem key={key} value={key}>{label}</SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            ROO Vinculado
                                        </Label>
                                        <Select 
                                            value={formData.roo_id || "none"} 
                                            onValueChange={(value) => setFormData({...formData, roo_id: value === "none" ? "" : value})}
                                        >
                                            <SelectTrigger data-testid="select-pa-roo" className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                <SelectValue placeholder="Selecione..." />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                <SelectItem value="none">Nenhum</SelectItem>
                                                {roos.map((roo) => (
                                                    <SelectItem key={roo.id} value={roo.id}>
                                                        {roo.codigo}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Sinistro Vinculado
                                    </Label>
                                    <Select 
                                        value={formData.sinistro_id || "none"} 
                                        onValueChange={(value) => setFormData({...formData, sinistro_id: value === "none" ? "" : value})}
                                    >
                                        <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                            <SelectValue placeholder="Selecione..." />
                                        </SelectTrigger>
                                        <SelectContent className="bg-slate-900 border-slate-800">
                                            <SelectItem value="none">Nenhum</SelectItem>
                                            {sinistros.map((sin) => (
                                                <SelectItem key={sin.id} value={sin.id}>
                                                    {sin.codigo}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Descrição *
                                    </Label>
                                    <Textarea
                                        data-testid="input-pa-descricao"
                                        value={formData.descricao}
                                        onChange={(e) => setFormData({...formData, descricao: e.target.value})}
                                        required
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[80px]"
                                    />
                                </div>

                                {/* Contacts Section */}
                                <div className="border-t border-slate-800 pt-4">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Contatos para Acionamento
                                    </Label>
                                    
                                    <div className="grid grid-cols-4 gap-2 mt-3">
                                        <Input
                                            placeholder="Nome"
                                            value={newContato.nome}
                                            onChange={(e) => setNewContato({...newContato, nome: e.target.value})}
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm text-sm"
                                        />
                                        <Input
                                            placeholder="Cargo"
                                            value={newContato.cargo}
                                            onChange={(e) => setNewContato({...newContato, cargo: e.target.value})}
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm text-sm"
                                        />
                                        <Input
                                            placeholder="Telefone"
                                            value={newContato.telefone}
                                            onChange={(e) => setNewContato({...newContato, telefone: e.target.value})}
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm text-sm"
                                        />
                                        <Button
                                            type="button"
                                            onClick={addContato}
                                            className="bg-slate-700 hover:bg-slate-600 text-white rounded-sm text-sm"
                                        >
                                            Adicionar
                                        </Button>
                                    </div>

                                    {formData.contatos.length > 0 && (
                                        <div className="mt-3 space-y-2">
                                            {formData.contatos.map((contato, idx) => (
                                                <div key={idx} className="flex items-center justify-between p-2 bg-slate-800/50 rounded-sm">
                                                    <div className="flex items-center gap-3">
                                                        <User className="w-4 h-4 text-cyan-400" />
                                                        <span className="text-sm text-white">{contato.nome}</span>
                                                        <span className="text-xs text-slate-400">{contato.cargo}</span>
                                                        <span className="text-xs font-mono text-cyan-400">{contato.telefone}</span>
                                                    </div>
                                                    <Button
                                                        type="button"
                                                        variant="ghost"
                                                        size="sm"
                                                        onClick={() => removeContato(idx)}
                                                        className="text-red-400 hover:text-red-300"
                                                    >
                                                        ×
                                                    </Button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => setDialogOpen(false)}
                                        className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        data-testid="btn-save-pa"
                                        className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm"
                                    >
                                        Criar PA
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
            </div>

            {/* Table */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardContent className="p-0">
                    {pas.length === 0 ? (
                        <div className="p-12 text-center">
                            <Phone className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                            <p className="text-slate-400">Nenhum PA registrado</p>
                        </div>
                    ) : (
                        <Table className="table-naval">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Código</TableHead>
                                    <TableHead>Título</TableHead>
                                    <TableHead>Criticidade</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>SLA</TableHead>
                                    <TableHead>Data</TableHead>
                                    <TableHead>Ações</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {pas.map((pa) => (
                                    <TableRow key={pa.id} data-testid={`pa-row-${pa.id}`}>
                                        <TableCell className="font-mono text-emerald-400">
                                            {pa.codigo}
                                        </TableCell>
                                        <TableCell className="text-white">
                                            {pa.titulo}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`
                                                px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                                ${pa.criticidade === 'baixa' ? 'bg-slate-800 text-slate-400 border border-slate-700' : ''}
                                                ${pa.criticidade === 'media' ? 'badge-info' : ''}
                                                ${pa.criticidade === 'alta' ? 'badge-warning' : ''}
                                                ${pa.criticidade === 'critica' ? 'badge-danger' : ''}
                                            `}>
                                                {criticidadeLabels[pa.criticidade]}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <span className={`
                                                px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                                ${pa.status === 'aberto' ? 'badge-warning' : ''}
                                                ${pa.status === 'em_tratativa' ? 'badge-info' : ''}
                                                ${pa.status === 'encerrado' ? 'badge-success' : ''}
                                            `}>
                                                {statusLabels[pa.status]}
                                            </span>
                                        </TableCell>
                                        <TableCell className="text-slate-400 font-mono text-xs">
                                            {pa.sla_minutos} min
                                        </TableCell>
                                        <TableCell className="text-slate-500 text-xs font-mono">
                                            {formatDate(pa.criado_em)}
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                    setSelectedPA(pa);
                                                    setDetailDialogOpen(true);
                                                }}
                                                className="text-slate-400 hover:text-white hover:bg-slate-800"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Detail Dialog */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                            <Phone className="w-5 h-5 text-emerald-400" />
                            {selectedPA?.codigo}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedPA && (
                        <div className="space-y-4 mt-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Título</p>
                                    <p className="text-white mt-1">{selectedPA.titulo}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">SLA</p>
                                    <p className="text-cyan-400 mt-1 font-mono">{selectedPA.sla_minutos} minutos</p>
                                </div>
                            </div>

                            <div>
                                <p className="text-xs text-slate-500 font-mono uppercase">Descrição</p>
                                <p className="text-slate-300 mt-1 text-sm">{selectedPA.descricao}</p>
                            </div>

                            {/* Contacts */}
                            {selectedPA.contatos?.length > 0 && (
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase mb-3">Contatos</p>
                                    <div className="space-y-2">
                                        {selectedPA.contatos.map((contato, idx) => (
                                            <div key={idx} className="flex items-center gap-4 p-3 bg-slate-800/50 rounded-sm">
                                                <User className="w-5 h-5 text-cyan-400" />
                                                <div>
                                                    <p className="text-white text-sm">{contato.nome}</p>
                                                    <p className="text-xs text-slate-400">{contato.cargo}</p>
                                                </div>
                                                <div className="ml-auto text-right">
                                                    <p className="text-cyan-400 font-mono text-sm">{contato.telefone}</p>
                                                    {contato.email && (
                                                        <p className="text-xs text-slate-500">{contato.email}</p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Tentativas */}
                            {selectedPA.tentativas?.length > 0 && (
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase mb-3 flex items-center gap-2">
                                        <Clock className="w-3 h-3" />
                                        Tentativas de Contato
                                    </p>
                                    <div className="space-y-2">
                                        {selectedPA.tentativas.map((tent, idx) => (
                                            <div key={idx} className="p-2 bg-slate-800/30 rounded-sm text-sm">
                                                <div className="flex items-center justify-between">
                                                    <span className="text-white">{tent.contato_nome}</span>
                                                    <span className={tent.sucesso ? 'text-emerald-400' : 'text-red-400'}>
                                                        {tent.sucesso ? 'Sucesso' : 'Sem resposta'}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-slate-500 mt-1">
                                                    {formatDate(tent.horario)} - {tent.registrado_por}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Actions */}
                            {selectedPA.status !== 'encerrado' && (
                                <div className="border-t border-slate-800 pt-4 flex gap-2">
                                    {selectedPA.status === 'aberto' && (
                                        <Button
                                            onClick={() => handleStatusChange(selectedPA.id, 'em_tratativa')}
                                            className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm text-xs"
                                        >
                                            Iniciar Acionamentos
                                        </Button>
                                    )}
                                    {canValidate && (
                                        <Button
                                            onClick={() => handleStatusChange(selectedPA.id, 'encerrado')}
                                            className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-sm text-xs"
                                        >
                                            Encerrar PA
                                        </Button>
                                    )}
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default PAPage;
